import { DATA_TYPE } from "@/modules/DashboardTemplate/constants/DashboardTemplatesConstants";
import { BASICO } from "@/shared/constants/GeneralConstants";

const NUMERIC_OPERATORS = ">,<,>=,<=,=,Entre";
const DATE_OPERATORS = ">,<,>=,<=,=,Entre,Rango de fechas";

/** Estrategia de campo para filtros del dashboard (tipo u operadores pueden derivarse del campo). */
export type DashboardFieldStrategy = {
  condition: (field: any) => boolean;
  type: string | ((field: any) => string);
  operators: string | ((field: any) => string);
};

/**
 * Campo definido como bloque HTML en el schema (`x-display: html` o `htmlContent`).
 */
export function isHtmlFieldStrategy(field: any): boolean {
  const def = field?.schema?.[field?.key];
  if (!def || typeof def !== "object") {
    return false;
  }
  const d = def as Record<string, unknown>;
  return d["x-display"] === "html" || typeof d.htmlContent === "string";
}

/**
 * Inspecciona `htmlContent` del campo y deduce tipo de filtro y operadores.
 * Prioridad si hay varios controles: fecha → numérico → select → resto (texto/HTML).
 */
export function resolveHtmlDashboardFilterMeta(
  field: any
): {
  type: string;
  operators: string;
} {
  const def = field?.schema?.[field?.key];
  const html =
    typeof def?.htmlContent === "string" ? def.htmlContent.trim() : "";
  if (!html) {
    return { type: DATA_TYPE.HTML, operators: "=" };
  }

  const hasDateInput = /<input\b[^>]*\btype\s*=\s*["']?(?:date|datetime-local|time|month|week)["']?[^>]*>/i.test(
    html
  );
  const hasNumericInput = /<input\b[^>]*\btype\s*=\s*["']?(?:number|range)["']?[^>]*>/i.test(
    html
  );

  if (hasDateInput) {
    return {
      type: DATA_TYPE.TIME_STAMP,
      operators: DATE_OPERATORS
    };
  }
  if (hasNumericInput) {
    return {
      type: DATA_TYPE.NUMBER,
      operators: NUMERIC_OPERATORS
    };
  }
  if (/<select\b/i.test(html)) {
    return { type: DATA_TYPE.HTML, operators: "=" };
  }

  return { type: DATA_TYPE.HTML, operators: "=" };
}

export const FIELD_TYPE_STRATEGIES: DashboardFieldStrategy[] = [
  {
    condition: isHtmlFieldStrategy,
    type: (field: any) => resolveHtmlDashboardFilterMeta(field).type,
    operators: (field: any) => resolveHtmlDashboardFilterMeta(field).operators
  },
  {
    condition: (field: any) =>
      field.type === BASICO &&
      field.schema[field.key]?.type === DATA_TYPE.STRING &&
      field.schema[field.key]?.format === DATA_TYPE.DATE,
    type: DATA_TYPE.TIME_STAMP,
    operators: DATE_OPERATORS
  },
  {
    condition: (field: any) =>
      field.type === "formula" &&
      field.schema[field.key]?.type === DATA_TYPE.STRING,
    type: DATA_TYPE.NUMBER,
    operators: NUMERIC_OPERATORS
  },
  {
    condition: (field: any) =>
      field.type === BASICO &&
      field.schema[field.key]?.type === DATA_TYPE.NUMBER,
    type: DATA_TYPE.NUMBER,
    operators: NUMERIC_OPERATORS
  },
  {
    condition: (field: any) => field.numberList,
    type: DATA_TYPE.NUMBER,
    operators: "="
  },
  {
    condition: (field: any) =>
      field.type === DATA_TYPE.LIST || field.type === DATA_TYPE.LISTA,
    type: DATA_TYPE.LIST,
    operators: "="
  }
];
